// tool_calls. The dispatcher below validates + applies them to app.paramGraph.

import { app } from '../store.svelte';
import { NODE_REGISTRY, nodesByCategory } from '../../core/paramNodes';
import { newId, evalGraph, sitOnPlate, type ParamNode, type Wire } from '../../core/paramGraph';
import { applyPatternDisplacement } from '../../core/cpuDisplace';
import type { Triangle } from '../../core/types';
import { svgToTextureGrid } from '../patterns';
import type { ToolDef } from './falProvider';

export function evaluateParamGraph(): void {
  const r = evalGraph(app.paramGraph, NODE_REGISTRY);
  const seated = sitOnPlate(r.triangles);
  app.triangles = app.paramPattern
    ? applyPatternDisplacement(seated, app.paramPattern)
    : seated;
}

// ---- Tool schemas ----

export const TOOL_DEFS: ToolDef[] = [
  {
    type: 'function',
    function: {
      name: 'list_node_types',
      description: 'Mevcut tüm node tiplerini listele (kategori + tip + giriş/çıkış portları). Yeni node eklemeden önce bunu çağır.',
      parameters: { type: 'object', properties: {}, required: [] }
    }
  },
  {
    type: 'function',
    function: {
      name: 'get_graph',
      description: 'Mevcut graph durumunu özet olarak getir: node listesi (id, tip, parametreler) + wire listesi.',
      parameters: { type: 'object', properties: {}, required: [] }
    }
  },
  {
    type: 'function',
    function: {
      name: 'add_node',
      description: 'Graph\'a yeni bir node ekle. type, NODE_REGISTRY\'deki anahtardan biri olmalı (örn "primitive.box"). x/y opsiyonel canvas pozisyonu (px). params object\'i node\'un giriş portlarına başlangıç literalları sağlar.',
      parameters: {
        type: 'object',
        properties: {
          type: { type: 'string', description: 'Node type slug, e.g. "primitive.cylinder"' },
          x: { type: 'number', description: 'Canvas X pozisyonu (px). Default 100.' },
          y: { type: 'number', description: 'Canvas Y pozisyonu (px). Default 100.' },
          params: { type: 'object', description: 'Port adı → değer (number/array) dictionary' }
        },
        required: ['type']
      }
    }
  },
  {
    type: 'function',
    function: {
      name: 'connect_wire',
      description: 'İki node arasında wire oluştur. fromPort source node\'un çıkışı, toPort target node\'un girişi olmalı. Tip uyumluluğu zorunlu.',
      parameters: {
        type: 'object',
        properties: {
          fromNode: { type: 'string' },
          fromPort: { type: 'string' },
          toNode: { type: 'string' },
          toPort: { type: 'string' }
        },
        required: ['fromNode', 'fromPort', 'toNode', 'toPort']
      }
    }
  },
  {
    type: 'function',
    function: {
      name: 'set_param',
      description: 'Bir node\'un parametre değerini güncelle (sadece wire bağlı OLMAYAN portlar için anlamlı).',
      parameters: {
        type: 'object',
        properties: {
          nodeId: { type: 'string' },
          portId: { type: 'string' },
          value: { description: 'number, array, ya da string' }
        },
        required: ['nodeId', 'portId', 'value']
      }
    }
  },
  {
    type: 'function',
    function: {
      name: 'delete_node',
      description: 'Bir node\'u (ve bağlı wire\'larını) sil.',
      parameters: {
        type: 'object',
        properties: { nodeId: { type: 'string' } },
        required: ['nodeId']
      }
    }
  },
  {
    type: 'function',
    function: {
      name: 'clear_graph',
      description: 'Graph\'ı tamamen temizle (tüm node ve wire silinir). Sıfırdan başlamak için.',
      parameters: { type: 'object', properties: {}, required: [] }
    }
  }
];

// ---- Dispatcher ----

export interface DispatchResult {
  ok: boolean;
  message: string;
  /** For add_node: the real node id assigned by the graph. Used by the
   *  placeholder resolver to map "n1"/"n2"/... → actual ids when the LLM
   *  references them in subsequent connect_wire calls. */
  nodeId?: string;
}

export async function dispatchTool(name: string, args: Record<string, unknown>): Promise<DispatchResult> {
  switch (name) {
    case 'list_node_types': {
      const cats = nodesByCategory();
      const out: string[] = [];
      for (const [cat, defs] of cats) {
        out.push(`\n[${cat}]`);
        for (const d of defs) {
          const ins = d.inputs.map((p) => `${p.id}:${p.type}`).join(', ');
          const outs = d.outputs.map((p) => `${p.id}:${p.type}`).join(', ');
          out.push(`  ${d.type} (${d.label}) — in:[${ins}] out:[${outs}]`);
        }
      }
      return { ok: true, message: out.join('\n') };
    }

    case 'get_graph': {
      const nodes = app.paramGraph.nodes.map((n) => ({
        id: n.id,
        type: n.type,
        x: Math.round(n.x),
        y: Math.round(n.y),
        params: n.params
      }));
      const wires = app.paramGraph.wires.map((w) => ({
        id: w.id,
        from: `${w.fromNode}.${w.fromPort}`,
        to: `${w.toNode}.${w.toPort}`
      }));
      return { ok: true, message: JSON.stringify({ nodes, wires }, null, 2) };
    }

    case 'add_node': {
      const type = String(args.type ?? '');
      const def = NODE_REGISTRY.get(type);
      if (!def) return { ok: false, message: `bilinmeyen node tipi "${type}"` };
      const params: Record<string, unknown> = {};
      for (const p of def.inputs) {
        if (p.defaultValue !== undefined) params[p.id] = p.defaultValue;
      }
      const userParams = (args.params as Record<string, unknown> | undefined) ?? {};
      for (const [k, v] of Object.entries(userParams)) params[k] = v;
      const node: ParamNode = {
        id: newId('n'),
        type,
        x: typeof args.x === 'number' ? args.x : 100 + Math.random() * 400,
        y: typeof args.y === 'number' ? args.y : 100 + Math.random() * 300,
        params,
        label: typeof args.label === 'string' ? args.label : undefined,
        portLabels: (args.portLabels as Record<string, string> | undefined),
        group: typeof args.group === 'string' ? args.group : undefined
      };
      app.paramGraph = { nodes: [...app.paramGraph.nodes, node], wires: app.paramGraph.wires };
      const tag = node.label ? ` "${node.label}"` : '';
      return { ok: true, message: `+ ${def.label}${tag}`, nodeId: node.id };
    }

    case 'set_node_meta': {
      const id = String(args.nodeId ?? '');
      const node = app.paramGraph.nodes.find((n) => n.id === id);
      if (!node) return { ok: false, message: `node bulunamadı: ${id}` };
      if (typeof args.label === 'string') node.label = args.label;
      if (typeof args.group === 'string') node.group = args.group;
      if (args.portLabels && typeof args.portLabels === 'object') {
        node.portLabels = { ...(node.portLabels ?? {}), ...(args.portLabels as Record<string, string>) };
      }
      app.paramGraph = { ...app.paramGraph };
      return { ok: true, message: `meta güncellendi: ${node.label ?? id}` };
    }

    case 'connect_wire': {
      const fromNode = String(args.fromNode ?? '');
      const fromPort = String(args.fromPort ?? '');
      const toNode = String(args.toNode ?? '');
      const toPort = String(args.toPort ?? '');
      const fn = app.paramGraph.nodes.find((n) => n.id === fromNode);
      const tn = app.paramGraph.nodes.find((n) => n.id === toNode);
      if (!fn || !tn) return { ok: false, message: `node bulunamadı (${fromNode} → ${toNode})` };
      const fdef = NODE_REGISTRY.get(fn.type);
      const tdef = NODE_REGISTRY.get(tn.type);
      if (!fdef || !tdef) return { ok: false, message: `node tipi tanımsız` };
      const fp = fdef.outputs.find((p) => p.id === fromPort);
      const tp = tdef.inputs.find((p) => p.id === toPort);
      if (!fp) return { ok: false, message: `"${fn.type}" üzerinde "${fromPort}" çıkış portu yok` };
      if (!tp) return { ok: false, message: `"${tn.type}" üzerinde "${toPort}" giriş portu yok` };
      if (fp.type !== tp.type) {
        return { ok: false, message: `tip uyuşmazlığı — ${fp.type} → ${tp.type}` };
      }
      const wires = app.paramGraph.wires.filter((w) => !(w.toNode === toNode && w.toPort === toPort));
      const wire: Wire = { id: newId('w'), fromNode, fromPort, toNode, toPort };
      wires.push(wire);
      app.paramGraph = { ...app.paramGraph, wires };
      return { ok: true, message: `↔ ${fdef.label}.${fromPort} → ${tdef.label}.${toPort}` };
    }

    case 'set_param': {
      const nodeId = String(args.nodeId ?? '');
      const portId = String(args.portId ?? '');
      const node = app.paramGraph.nodes.find((n) => n.id === nodeId);
      if (!node) return { ok: false, message: `node bulunamadı: ${nodeId}` };
      node.params[portId] = args.value;
      app.paramGraph = { ...app.paramGraph };
      return { ok: true, message: `${nodeId}.${portId} = ${JSON.stringify(args.value)}` };
    }

    case 'delete_node': {
      const id = String(args.nodeId ?? '');
      const before = app.paramGraph.nodes.length;
      app.paramGraph = {
        nodes: app.paramGraph.nodes.filter((n) => n.id !== id),
        wires: app.paramGraph.wires.filter((w) => w.fromNode !== id && w.toNode !== id)
      };
      return before === app.paramGraph.nodes.length
        ? { ok: false, message: `node bulunamadı` }
        : { ok: true, message: `silindi: ${id}` };
    }

    case 'clear_graph': {
      app.paramGraph = { nodes: [], wires: [] };
      return { ok: true, message: 'graph temizlendi' };
    }

    case 'set_vase_surface_pattern': {
      // Rasterizes an SVG pattern and applies it as cylindrical-UV CPU
      // displacement to the param-graph mesh (NOT the legacy vase shader).
      // Stored in app.paramPattern; runEval applies it on every subsequent
      // graph eval so the displacement persists across param tweaks.
      const svg = String(args.svg ?? '').trim();
      if (!svg.startsWith('<')) return { ok: false, message: 'SVG boş veya geçersiz (root <svg> bekleniyor)' };
      const intensity = typeof args.intensity === 'number' ? args.intensity : 2.5;
      const repeatU = typeof args.repeatU === 'number' ? args.repeatU : 6;
      const repeatV = typeof args.repeatV === 'number' ? args.repeatV : 4;
      try {
        const grid = await svgToTextureGrid(svg, intensity, repeatU, repeatV, app.textureResolution);
        app.paramPattern = { grid, intensity, repeatU, repeatV };
        // Trigger an immediate re-eval so the user sees the change without
        // needing to nudge a slider. Mirrors what ParamControlsOverlay would
        // do on the next param tweak.
        const r = evalGraph(app.paramGraph, NODE_REGISTRY);
        const seated = sitOnPlate(r.triangles);
        app.triangles = applyPatternDisplacement(seated, app.paramPattern);
        return { ok: true, message: `SVG pattern uygulandı (intensity=${intensity}, repeat=${repeatU}×${repeatV}) — param graph mesh'ine CPU displacement` };
      } catch (err) {
        return { ok: false, message: 'SVG → displacement başarısız: ' + (err as Error).message };
      }
    }

    case 'clear_vase_surface_pattern': {
      app.paramPattern = null;
      const r = evalGraph(app.paramGraph, NODE_REGISTRY);
      app.triangles = sitOnPlate(r.triangles);
      return { ok: true, message: 'SVG pattern temizlendi' };
    }

    default:
      return { ok: false, message: `bilinmeyen tool "${name}"` };
  }
}

/** System prompt — orientates the model on what ClayForge is and forces the
 *  structured <actions>+<reply> output protocol. Verified working with
 *  openai/gpt-5-chat on FAL.AI any-llm endpoint. */
export const SYSTEM_PROMPT = `Sen ClayForge'un parametrik modelleme asistanısın. ClayForge bir kil 3D printer slicer'ı; vazo/kase/organik form üretimi için Grasshopper-tarzı node-graph editörü var.

KULLANICI YANITINI ŞU FORMATTA VER (her zaman):

<actions>
[
  { "tool": "TOOL_ADI", "args": { ... } },
  { "tool": "TOOL_ADI", "args": { ... } }
]
</actions>
<reply>
Türkçe, kısa, doğal — kullanıcıya ne yaptığını açıkla.
</reply>

KULLANILABİLİR TOOLLAR (her biri JSON args alır):

1. add_node — args: { type, x?, y?, params?, label?, portLabels?, group? }
   - label: bu node'un domain'e özel adı (örn "Kabinet gövdesi", "Çekmece yüzü", "Ayak"). KULLAN.
   - portLabels: { portId → semantik isim } — generic portları domain isimlerine çevir
     (örn primitive.box için { sx: "Çekmece genişliği", sy: "Çekmece derinliği", sz: "Yüzey kalınlığı" })
   - group: ilgili node'ların kümeleneceği grup (örn "Kabinet", "Çekmece", "Ayaklar"). Aynı parça
     ailesindeki tüm node'lar aynı group string'ini kullanmalı — overlay onları birlikte gösterir.
2. set_node_meta — args: { nodeId, label?, group?, portLabels? } — sonradan isim/grup güncelleme
3. connect_wire — args: { fromNode, fromPort, toNode, toPort }
4. set_param — args: { nodeId, portId, value }
5. delete_node — args: { nodeId }
6. clear_graph — args: {}
7. set_vase_surface_pattern — args: { svg, intensity?, repeatU?, repeatV? }
   - Param graph mesh'inin TAMAMINA silindirik UV ile SVG-tabanlı kabartma displacement uygular (CPU; param-graph vazo, kapağı, kulpları HEPSI etkilenir).
   - Küçük primitive'leri tek tek polar dizide vazoya yapıştırma — onun yerine BU TOOL'u kullan.
   - SVG'yi sen üret: 100×100 viewBox'lı küçük bir tile motifi yaz (siyah dolgu = kabartma yüksekliği). Pattern repeatU/repeatV ile mesh'in çevresine ve yüksekliğine sarılır.
   - Örnek SVG'ler: baklava, çini, pul, dama, dalga, yaprak — sade silüet motifleri en iyi sonucu verir.
   - Kullanıcı "vazonun üstüne SVG pattern / kabartma desen / motif baskısı" istediğinde MUTLAKA bu tool'u çağır, primitive eklemeye kalkma.
   - intensity 1.5–3.5 arası iyi (default 2.5), repeatU 4–10, repeatV 2–6.
   - Pattern app.paramPattern'a yazılır → her slider tweak sonrası otomatik yeniden uygulanır.
8. clear_vase_surface_pattern — args: {} (mevcut pattern'ı kaldırır, mesh düz yüzeye döner)

NODE PORT ŞEMASI (giriş portları):
- primitive.box      in: sx, sy, sz                              out: mesh   (origin merkezli kutu)
- primitive.cylinder in: r, h, segments                          out: mesh   (z ekseninde dikey, taban z=0)
- primitive.tube     in: r, h, axis (0=X 1=Y 2=Z), segments      out: mesh   (eksen seçilebilir silindir — kulp/aks/dik bar için ideal)
- primitive.cone     in: rBot, rTop, h, segments                 out: mesh   (z ekseninde dikey)
- primitive.sphere   in: r, segments, rings                      out: mesh   (origin merkezli)
- primitive.ellipsoid in: rx, ry, rz, segments, rings            out: mesh   (organik form, yumurta/damla/yastık)
- primitive.capsule  in: r, h, segments, rings                   out: mesh   (z ekseninde kapsül — rounded bar, organik uzuv)
- primitive.prism    in: r, h, sides                             out: mesh   (n-kenarlı dikey prizma — altıgen kalem, sekizgen kolon)
- primitive.torus    in: R, r, segments, sides                   out: mesh   (z eksenine dik düzlemde donut)
- primitive.ring     in: rOuter, rInner, h, segments             out: mesh   (yassı halka/rondelle/çerçeve — z=0..h dilim)
- primitive.vase     in: height, bottomR, bellyR, topR, segments, rings  out: mesh
- transform.translate in: mesh, x, y, z                          out: mesh
- transform.rotate   in: mesh, axis (0=X 1=Y 2=Z), angle (°)     out: mesh   (origin etrafında)
- transform.scale    in: mesh, sx, sy, sz                        out: mesh   (origin etrafında)
- combine.merge      in: a (mesh), b (mesh)                      out: mesh
- array.linear       in: mesh, count, dx, dy, dz                 out: mesh
- array.grid         in: mesh, countX, countY, dx, dy            out: mesh   (xy düzleminde 2D ızgara, origin merkezli)
- array.polar        in: mesh, count, radius                     out: mesh   (z ekseni etrafında, mesh radius mesafede)
- math.add/multiply/sin, value.number, value.vec3, output.mesh   (önceki gibi)

CSG (Boole — gerçek katı modelleme):
- csg.union      in: a (mesh), b (mesh)                          out: mesh   (iki mesh'in tam birleşimi — iç yüzeyler temizlenir, sadece dış kabuk kalır)
- csg.subtract   in: a (mesh, gövde), b (mesh, delik)            out: mesh   (A'dan B'yi çıkarır — delik açma, oyma, kanal açma)
- csg.intersect  in: a (mesh), b (mesh)                          out: mesh   (sadece ortak hacim — clipping, fillet hazırlığı)
   - csg.* mesh'lerin KAPALI manifold olmasını ister (vase'in kapağı yoksa veya yüzey açıksa garip sonuç verir).
   - Yüksek poly mesh'ler (>3000 tri) yavaş olur; gerektiğinde primitive'lerin segments'ini düşür.

PROFİL & YÜZEY (organik karmaşık formlar):
- profile.circle    in: r, segments                              out: profile  (XY düzleminde dairesel çıkış)
- profile.rect      in: sx, sy                                   out: profile  (4 köşeli)
- profile.polygon   in: r, sides                                 out: profile  (n-kenarlı)
- path.line         in: x0,y0,z0, x1,y1,z1, segments             out: path     (doğrusal yol — sweep için)
- path.arc          in: r, startDeg, endDeg, cz, segments        out: path     (yay yol — kulp eğrisi)
- surface.loft      in: a (profile), b (profile), z0, z1, capBot, capTop  out: mesh
                    (iki profil arasında tünel — koni, vazo gövdesi, organik geçiş)
- surface.revolve   in: profile, segments                        out: mesh
                    (profil = (radius, _, height) noktaları → Z ekseninde devrim — kase, vazo, kadeh)
- surface.sweep     in: profile, path                            out: mesh
                    (profil yol boyunca süpürülür — kulp, boru, hortum, organik uzuv)

UZAYSAL KURALLAR (KRİTİK — model bunlara dikkat et):
- ClayForge'da +Z yukarı, x-y zemin (build plate). Tüm mesh'ler z=0'a snap edilir.
- primitive.vase z=0'dan z=height'e uzanır. bellyR varsayılan olarak ortada (height/2).
- Vazo karın yarıçapı bir z için yaklaşık olarak: lerp(bottomR, bellyR, z/(height/2)) (alt yarı), lerp(bellyR, topR, (z-height/2)/(height/2)) (üst yarı).
- Vazo YÜZEYINE bir motif yerleştirmek için: o yükseklikteki yaklaşık yarıçapı hesapla, motifi (translate) o yarıçapa götür, sonra polar.array ile etrafa dağıt.
- KULPLU vazo için: torus (R = vazo R + kulp arc/2, r = kulp kalınlığı) → rotate axis=0 angle=90 (dik) → translate x=R+gap, z=mid → polar count=2 radius=0 (radius=0 → mesh kendi yerinde döner = simetri).
- Bant/halka dekorasyon: tek primitive.torus yeter (büyük R = vazo karın R, küçük r = ~2-4mm).
- Yassı motif/madalyon: primitive.ring veya küçük ellipsoid → translate yarıçapa → polar count radius=0.
- Kapak: silindir (r = vazo topR + 2..4mm pay, h = 4..8mm) → translate z = vazo height (yükseklikten oku!) → opsiyonel topuz (sphere/ellipsoid) translate z = vazo height + lid_h + topuz_r.
- Kulpu hızlı modellemek için primitive.tube axis=0 (yatay silindir) + translate, rotate hiç gerekmez.

DÜZENLEME / EKLEME AKIŞI (ÇOK ÖNEMLİ):
Her chat mesajının başında "MEVCUT GRAPH SNAPSHOT" verilir. Snapshot'ta her node'un id'si, label'ı, type'ı, bbox'u (x/y/z aralıkları, mm cinsinden) ve param değerleri yazar. Toplam görünür mesh bbox'u da var.

KULLANICI BİR ŞEY EKLEMENİ İSTERSE:
1. Önce snapshot'tan ilgili parçanın bbox'ını oku (örn vazo: bbox=[..,..,0..180] → height=180, topR snapshot params'tan)
2. Yeni parçayı bbox koordinatlarına göre konumlandır (kapak için: cylinder z = 180, topuz z = 180 + lid_h)
3. clear_graph ÇAĞIRMA — sadece add_node + connect_wire ile yeni parçaları ekle, mevcut output.mesh'in birleştir zincirine bağla
4. Yeni node'lara semantic label/group/portLabels ver
5. Reply'da hangi koordinatlara yerleştirdiğini söyle ("kapak z=180 yüksekliğine, vazo topR=30 + 3mm payla r=33 koydum")

KULLANICI BİR ŞEY DEĞİŞTİRMENİ İSTERSE:
1. Snapshot'tan ilgili node id'sini bul (label ile eşleştir — "vazoyu büyüt" → "Vazo gövdesi" label'lı node)
2. set_param ile değiştir, gerekirse set_node_meta ile etiket güncelle
3. clear_graph ÇAĞIRMA, yeni node ekleme (mevcut graph'ı koru)

KULLANICI BİR ŞEY SİLMENİ İSTERSE:
- delete_node ile o id'yi kaldır
- Bağlı wire'lar otomatik temizlenir, ama merge zincirinin bütünlüğü bozulabilir — gerekirse yeniden wire kur

KURALLAR:
- HER GRAPH'TA bir "output.mesh" node'u OLMALI ve mesh wire'lı olmalı, yoksa kullanıcı 3D'de hiçbir şey görmez.
- Wire kurarken tip uyumluluğu zorunlu: number↔number, mesh↔mesh, vec3↔vec3.
- Node id'leri sen oluşturmuyorsun — add_node sonrası gerçek id verilir; sen geçici "n1", "n2" placeholder kullan, biz wire kurarken aynı sırayla map ederiz.
- Önce add_node çağrıları, sonra connect_wire çağrıları (sırayla execute edilir).
- Mevcut bir graph'ı değiştirmek istiyorsa kullanıcı, önce clear_graph çağırma — sadece set_param veya yeni node ekle.
- Numerik değerleri params içinde literal ver (örn { sx: 30 }).
- Kullanıcı "vazoyu büyüt" gibi diyorsa: clear_graph çağırma, var olan vase node'unun height/bottomR vs.'sini set_param ile artır.

SEMANTIK İSİMLENDİRME — KRİTİK:
Kullanıcı çekmeceli komidin, tabure, lambader gibi kompozit bir obje istediğinde:
- Her node'a label ver (kullanıcının anlayacağı domain ismi, generic "Kutu" değil)
- Generic portları (sx/sy/sz, count, dx) o parçanın gerçek anlamına göre portLabels ile yeniden adlandır
- İlgili node'ları aynı group altında topla (kabinet parçaları → "Kabinet", çekmeceler → "Çekmece", ayaklar → "Ayaklar")
- Kullanıcı viewport'taki overlay'de "Çekmece sayısı: 4" gibi bir slider görsün — "Sayı: 4" değil

ÖRNEK — "Çini bantlı, iki yanı kulplu, kapaklı vazo" (uzaysal kuralları doğru uygulayan referans):
<actions>
[
  { "tool": "add_node", "args": { "type": "primitive.vase", "x": 100, "y": 200,
      "label": "Vazo gövdesi", "group": "Gövde",
      "params": { "height": 180, "bottomR": 35, "bellyR": 55, "topR": 30 },
      "portLabels": { "height": "Yükseklik", "bottomR": "Taban yarıçapı", "bellyR": "Karın yarıçapı", "topR": "Ağız yarıçapı" }
  } },
  { "tool": "add_node", "args": { "type": "primitive.cylinder", "x": 100, "y": 380,
      "label": "Kapak diski", "group": "Kapak",
      "params": { "r": 32, "h": 6 } } },
  { "tool": "add_node", "args": { "type": "transform.translate", "x": 350, "y": 380,
      "label": "Kapağı ağza yerleştir", "group": "Kapak",
      "params": { "z": 180 } } },
  { "tool": "add_node", "args": { "type": "primitive.sphere", "x": 100, "y": 540,
      "label": "Kapak topuzu", "group": "Kapak",
      "params": { "r": 8 } } },
  { "tool": "add_node", "args": { "type": "transform.translate", "x": 350, "y": 540,
      "label": "Topuzu kapağın üstüne", "group": "Kapak",
      "params": { "z": 188 } } },

  { "tool": "add_node", "args": { "type": "primitive.torus", "x": 100, "y": 700,
      "label": "Sol kulp", "group": "Kulplar",
      "params": { "R": 22, "r": 4, "segments": 24, "sides": 10 } } },
  { "tool": "add_node", "args": { "type": "transform.rotate", "x": 350, "y": 700,
      "label": "Kulpu dik konuma", "group": "Kulplar",
      "params": { "axis": 0, "angle": 90 } } },
  { "tool": "add_node", "args": { "type": "transform.translate", "x": 600, "y": 700,
      "label": "Sol kulpu vazonun yan yüzeyine", "group": "Kulplar",
      "params": { "x": 55, "z": 90 } } },
  { "tool": "add_node", "args": { "type": "array.polar", "x": 850, "y": 700,
      "label": "İki kulp simetrisi", "group": "Kulplar",
      "params": { "count": 2, "radius": 0 },
      "portLabels": { "count": "Kulp sayısı" } } },

  { "tool": "add_node", "args": { "type": "primitive.torus", "x": 100, "y": 880,
      "label": "Üst çini bandı", "group": "Çini",
      "params": { "R": 52, "r": 2.5, "segments": 64, "sides": 8 } } },
  { "tool": "add_node", "args": { "type": "transform.translate", "x": 350, "y": 880,
      "label": "Bandı yukarı taşı", "group": "Çini",
      "params": { "z": 130 } } },
  { "tool": "add_node", "args": { "type": "primitive.torus", "x": 100, "y": 1020,
      "label": "Alt çini bandı", "group": "Çini",
      "params": { "R": 50, "r": 2.5, "segments": 64, "sides": 8 } } },
  { "tool": "add_node", "args": { "type": "transform.translate", "x": 350, "y": 1020,
      "label": "Alt bandı aşağı taşı", "group": "Çini",
      "params": { "z": 50 } } },

  { "tool": "add_node", "args": { "type": "combine.merge", "x": 1100, "y": 200, "label": "Gövde + Kapak", "group": "Birleştir" } },
  { "tool": "add_node", "args": { "type": "combine.merge", "x": 1100, "y": 380, "label": "+ Topuz", "group": "Birleştir" } },
  { "tool": "add_node", "args": { "type": "combine.merge", "x": 1100, "y": 560, "label": "+ Kulplar", "group": "Birleştir" } },
  { "tool": "add_node", "args": { "type": "combine.merge", "x": 1100, "y": 740, "label": "+ Üst çini", "group": "Birleştir" } },
  { "tool": "add_node", "args": { "type": "combine.merge", "x": 1100, "y": 920, "label": "+ Alt çini", "group": "Birleştir" } },
  { "tool": "add_node", "args": { "type": "output.mesh", "x": 1350, "y": 560, "label": "Çıktı", "group": "Çıktı" } },

  { "tool": "connect_wire", "args": { "fromNode": "n2", "fromPort": "mesh", "toNode": "n3", "toPort": "mesh" } },
  { "tool": "connect_wire", "args": { "fromNode": "n4", "fromPort": "mesh", "toNode": "n5", "toPort": "mesh" } },
  { "tool": "connect_wire", "args": { "fromNode": "n6", "fromPort": "mesh", "toNode": "n7", "toPort": "mesh" } },
  { "tool": "connect_wire", "args": { "fromNode": "n7", "fromPort": "mesh", "toNode": "n8", "toPort": "mesh" } },
  { "tool": "connect_wire", "args": { "fromNode": "n8", "fromPort": "mesh", "toNode": "n9", "toPort": "mesh" } },
  { "tool": "connect_wire", "args": { "fromNode": "n10", "fromPort": "mesh", "toNode": "n11", "toPort": "mesh" } },
  { "tool": "connect_wire", "args": { "fromNode": "n12", "fromPort": "mesh", "toNode": "n13", "toPort": "mesh" } },
  { "tool": "connect_wire", "args": { "fromNode": "n1", "fromPort": "mesh", "toNode": "n14", "toPort": "a" } },
  { "tool": "connect_wire", "args": { "fromNode": "n3", "fromPort": "mesh", "toNode": "n14", "toPort": "b" } },
  { "tool": "connect_wire", "args": { "fromNode": "n14", "fromPort": "mesh", "toNode": "n15", "toPort": "a" } },
  { "tool": "connect_wire", "args": { "fromNode": "n5", "fromPort": "mesh", "toNode": "n15", "toPort": "b" } },
  { "tool": "connect_wire", "args": { "fromNode": "n15", "fromPort": "mesh", "toNode": "n16", "toPort": "a" } },
  { "tool": "connect_wire", "args": { "fromNode": "n9", "fromPort": "mesh", "toNode": "n16", "toPort": "b" } },
  { "tool": "connect_wire", "args": { "fromNode": "n16", "fromPort": "mesh", "toNode": "n17", "toPort": "a" } },
  { "tool": "connect_wire", "args": { "fromNode": "n11", "fromPort": "mesh", "toNode": "n17", "toPort": "b" } },
  { "tool": "connect_wire", "args": { "fromNode": "n17", "fromPort": "mesh", "toNode": "n18", "toPort": "a" } },
  { "tool": "connect_wire", "args": { "fromNode": "n13", "fromPort": "mesh", "toNode": "n18", "toPort": "b" } },
  { "tool": "connect_wire", "args": { "fromNode": "n18", "fromPort": "mesh", "toNode": "n19", "toPort": "in" } }
]
</actions>
<reply>
180mm yüksekliğinde, kapaklı, iki kulplu, üst ve alt belde çini bandı olan bir vazo oluşturdum. Sağ panelden "Karın yarıçapı", "Kulp sayısı", "Üst çini bandı R" gibi tüm parametreleri ayarlayabilirsin.
</reply>`;

/** Resolve placeholder id (n1, n2, …) to real graph id created during this batch. */
export function makePlaceholderResolver(): {
  remember: (placeholder: string, realId: string) => void;
  resolve: (id: string) => string;
} {
  const map = new Map<string, string>();
  return {
    remember(p, r) { map.set(p, r); },
    resolve(id) { return map.get(id) ?? id; }
  };
}

/** Build a compact "what is currently in the model" snapshot the LLM sees on
 *  every chat turn. AGGRESSIVELY MINIMAL — every byte costs tokens, and
 *  prior versions of this function were the single biggest token sink in
 *  the chat loop (could hit 5k+ tokens per turn for a complex graph,
 *  multiplied by every retry → fast credit drain).
 *
 *  Shape per node: `id "label" type bbox=W×D×H @z=Zmin..Zmax`. No params,
 *  no wires, no per-axis bounds. Final overall bbox at the end.
 *  Typical output: ~80 bytes per node, ~500-1000 bytes for a 10-node graph. */
export function buildGraphContext(): string {
  const g = app.paramGraph;
  if (g.nodes.length === 0) return '[GRAPH BOŞ]';

  let result;
  try { result = evalGraph(g, NODE_REGISTRY); }
  catch { return '[GRAPH eval hata]'; }

  const lines: string[] = ['[MEVCUT GRAPH — ekleme/değişiklik istenirse aşağıdaki id\'lere ve koordinatlara göre konumlandır]'];
  for (const node of g.nodes) {
    const def = NODE_REGISTRY.get(node.type);
    const name = node.label ?? def?.label ?? node.type;
    const v = result.values.get(node.id);
    const meshOut = v?.mesh as Triangle[] | undefined;
    let bbox = '';
    if (Array.isArray(meshOut) && meshOut.length > 0) {
      let mnX = Infinity, mnY = Infinity, mnZ = Infinity;
      let mxX = -Infinity, mxY = -Infinity, mxZ = -Infinity;
      for (const t of meshOut) {
        for (const vt of [t.a, t.b, t.c]) {
          if (vt[0] < mnX) mnX = vt[0]; if (vt[0] > mxX) mxX = vt[0];
          if (vt[1] < mnY) mnY = vt[1]; if (vt[1] > mxY) mxY = vt[1];
          if (vt[2] < mnZ) mnZ = vt[2]; if (vt[2] > mxZ) mxZ = vt[2];
        }
      }
      bbox = ` ${(mxX - mnX).toFixed(0)}×${(mxY - mnY).toFixed(0)}×${(mxZ - mnZ).toFixed(0)}mm@z=${mnZ.toFixed(0)}..${mxZ.toFixed(0)}`;
    }
    lines.push(`  ${node.id} "${name}" ${node.type}${bbox}`);
  }
  if (app.triangles.length > 0) {
    let mnZ = Infinity, mxZ = -Infinity, mxR = 0;
    for (const t of app.triangles) for (const vt of [t.a, t.b, t.c]) {
      if (vt[2] < mnZ) mnZ = vt[2]; if (vt[2] > mxZ) mxZ = vt[2];
      const r = Math.hypot(vt[0], vt[1]);
      if (r > mxR) mxR = r;
    }
    lines.push(`  TOPLAM: maxR=${mxR.toFixed(0)}mm, z=${mnZ.toFixed(0)}..${mxZ.toFixed(0)}`);
  }
  return lines.join('\n');
}

/** Vision-mode system prompt — adds image-reasoning guidance on top of the
 *  base graph-mutation protocol. Used by the image pipeline (Stage 2). */
export const VISION_SYSTEM_PROMPT = SYSTEM_PROMPT + `

GÖRSELDEN MODELLEME — EK TALİMATLAR:

Kullanıcı 1 veya 2 görsel ekledi. İlk görsel orijinal referans fotoğraf, ikinci görsel (varsa) senin için üretilmiş 4-görünümlü teknik çizim sayfasıdır.

Görseli analiz ederken:
- Önce SİMETRİ tipini belirle: dönel mi (vazo, kase), iki-eksenli mi (kutu), düzlemsel mi?
- Dönel ise primitive.vase kullan (height, bottomR, bellyR, topR parametrelerini görselden tahmin et — pixel oranlarını mm'ye dök, varsayılan ölçek yükseklik 100-200mm).
- Kutu/silindir/koni varsa primitive.box/cylinder/cone kullan.
- Tekrar eden detaylar (kulp, dilim, çıkıntı) varsa array.linear veya array.polar kullan, count'u görselden say.
- Birleşik formlar için combine.merge zincirle.
- Asla photorealism eklemeye çalışma — ClayForge bir slicer, sadece kapsayıcı geometriyi üret.
- ÖLÇÜLER: kullanıcı ölçü vermediyse, görseldeki en uzun boyutu ~150mm olarak normalize et. Diğer boyutları oransal türet.
- Belirsizlik varsa <reply> içinde belirt ("varsayım: yükseklik 18cm, oranı korumak için karın çapı 6cm seçildi").

Bu görselden çalışırken zaman zaman teknik sayfadaki 4 görünümün (TOP/FRONT/SIDE/ISO) hangi parametreyi nasıl gösterdiğini de açıkla.`;
